import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import quoteService from '../../services/quoteService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import QuoteEditor, { QUOTE_STATUS } from '../../components/quotes/QuoteEditor';
import { rands } from '../../components/billing/TariffLines';

const VIEWS = [
  { key: 'awaiting', label: 'Awaiting response', tone: 'text-blue-700' },
  { key: 'declined', label: 'Declined', tone: 'text-red-700' },
  { key: 'expired', label: 'Expired', tone: 'text-amber-700' },
  { key: 'accepted', label: 'Accepted', tone: 'text-green-700' },
  { key: 'draft', label: 'Drafts', tone: 'text-slate-600' },
  { key: 'all', label: 'All', tone: 'text-slate-700' },
];
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—');

/**
 * Quotes & consent — every anaesthetic estimate sent to patients before surgery,
 * and whether they have accepted it. Quotes are made from a booked patient on the
 * Theatre Schedule; follow up here on the ones still waiting, declined or expired.
 */
const Quotes = () => {
  const admin = isAdmin();
  const [view, setView] = useState('awaiting');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await quoteService.list({ view }));
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-brand-deep">Quotes &amp; consent</h1>
        <p className="mt-1 text-slate-500">Estimates sent to patients before surgery — what their medical aid is likely to pay, what they’ll pay, and whether they’ve agreed.</p>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <span>Make a quote from a booked patient on the <b>Theatre Schedule</b> (open the session, then <b>Quote</b>). The patient gets a link to read the estimate and accept or decline it; the doctor is told either way.</span>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} className={`rounded-xl border px-3 py-2.5 text-left transition ${view === v.key ? 'border-brand-navy bg-white shadow-sm ring-2 ring-brand-navy/10' : 'border-slate-200 bg-white/70 hover:border-slate-300'}`}>
            <div className={`text-xs font-bold ${v.tone}`}>{v.label}</div>
            <div className="text-lg font-bold text-slate-800">{data ? data.counts[v.key] : '—'}</div>
          </button>
        ))}
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex justify-end border-b border-slate-100 px-4 py-2">
          <button onClick={load} title="Refresh" className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy"><ArrowPathIcon className="h-5 w-5" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Quote</th>
                <th className="px-2 py-2 font-semibold">Surgery</th>
                <th className="px-2 py-2 font-semibold">Patient</th>
                {admin && <th className="px-2 py-2 font-semibold">Doctor</th>}
                <th className="px-2 py-2 text-right font-semibold">Fee</th>
                <th className="px-2 py-2 text-right font-semibold">Patient pays</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 text-right font-semibold">Sent / answered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.quotes || []).map((q) => {
                const s = QUOTE_STATUS[q.status] || { label: q.status, tone: 'bg-slate-100' };
                return (
                  <tr key={q.quote_id} onClick={() => setOpen(q)} className="cursor-pointer align-top hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-mono font-semibold text-brand-navy">{q.quote_no}</td>
                    <td className="px-2 py-2.5 text-slate-600">{fmtDate(q.service_date)}<div className="text-[11px] text-slate-400">{q.hospital_name}</div></td>
                    <td className="px-2 py-2.5">
                      <div className="font-semibold text-slate-700">{q.patient_name}</div>
                      <div className="text-[11px] text-slate-500">{q.payer === 'private' ? 'Private' : q.scheme_name}</div>
                      {q.procedure_text && <div className="max-w-xs truncate text-[11px] text-slate-400">{q.procedure_text}</div>}
                    </td>
                    {admin && <td className="px-2 py-2.5 text-slate-600">{q.doctor}</td>}
                    <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-slate-700">{rands(q.practice_total)}</td>
                    <td className="whitespace-nowrap px-2 py-2.5 text-right font-semibold tabular-nums text-slate-800">{q.shortfall == null ? '?' : rands(q.shortfall)}</td>
                    <td className="px-2 py-2.5">
                      <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{s.label.replace('Quote ', '')}</span>
                      {q.status === 'declined' && q.response_note && <div className="mt-0.5 max-w-[12rem] truncate text-[11px] text-red-700">“{q.response_note}”</div>}
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">
                      {q.responded_at ? fmtWhen(q.responded_at) : q.sent_at ? `sent ${fmtWhen(q.sent_at)}` : '—'}
                      {q.status === 'sent' && <div className="text-[11px]">{q.opened_at ? 'opened' : 'not opened yet'}</div>}
                    </td>
                  </tr>
                );
              })}
              {data && data.quotes.length === 0 && <tr><td colSpan={admin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">No quotes here.</td></tr>}
              {!data && <tr><td colSpan={admin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {open && (
        <QuoteEditor
          bid={open.bid}
          patient={{ patient_id: open.patient_id, name: open.patient_name, email: open.patient_email, payer: open.payer, scheme_name: open.scheme_name }}
          bookingLabel={[fmtDate(open.service_date), open.hospital_name, open.surgeon].filter(Boolean).join(' · ')}
          plannedProcedure={open.procedure_text}
          onClose={() => { setOpen(null); load(); }}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default Quotes;
